/** @class ManageItemPermissionsModal
 *
 * @desc
 * Modal for managing advisor/firm item permissions
 *
 */

import {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Map} from 'immutable'

import ButtonGroup from '../../../../../../shared_components/core/buttons/ButtonGroup'
import ConfirmationCloser from '../../../../../../shared_components/ConfirmationCloser'
import Modals from '../../../../../../shared_components/core/modals/Modals'
import ItemPermissionsSelectForm from '../../../../../../shared_components/item_permissions/ItemPermissionsSelectForm'

import {Cancel, Accept} from '../../../blocks/Buttons'
import {ModalHeader} from '../../../../../../shared_components/core/modals/ModalHeaders'
import {addPossessive, arraysAreEqual} from '../../../../../../lib/tools'
import {capItemIdsToManageable, requestedItemPermissions} from '../../../../../../lib/itemPermissionTools'
import {useNotificationContext} from '../../../../../../shared_components/notifications/NotificationContext'

import './manageItemPermissionsModal.scss'

const ManageItemPermissionsModal = ({
  allItemIds,
  isFuneralHome,
  name,
  ownership,
  updateResource
}) => {
  const [isDirty, setIsDirty] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [checkedItemIds, setCheckedItemIds] = useState([])

  const {alwaysNotify} = useNotificationContext()

  // The persisted +edit-item-ids+ on the firm-ownership are not recomputed when
  // the AM tightens the firm's editable items preset, so cap them to
  // +manageable-edit-item-ids+ before priming the form. This keeps the +checked+
  // state aligned with the rows the +ItemPermissionsSelectForm+ actually renders
  // (restricted ids would otherwise be invisibly checked) and ensures the dirty
  // comparison below reflects the user's effective starting point.
  const cappedEditItemIds = () => capItemIdsToManageable(
    ownership.get('edit-item-ids').toArray(),
    ownership.get('manageable-edit-item-ids')
  )

  const toggleModal = () => {
    setIsDirty(false)
    setShowModal(prevShowModal => !prevShowModal)
    setProcessing(false)
    setCheckedItemIds(cappedEditItemIds())
  }

  const closer = () => (
    <ConfirmationCloser
      close={toggleModal}
      confirmButtonText={'Discard'}
      confirmClose={isDirty}
      headingText={'Discard changes you\'ve made?'}
    />
  )

  // Show the compliance note when the firm's manageable universe is narrower
  // than the full metadata-item universe in the app.
  const modalHeaderNote = useMemo(() => {
    const manageableEditItemIds = ownership.get('manageable-edit-item-ids')
    if (!manageableEditItemIds) return null
    if (allItemIds.length === 0) return null
    if (manageableEditItemIds.size === allItemIds.length) return null
    return `Note: some sections may be restricted by the compliance settings of ${addPossessive(name)} firm.`
  }, [allItemIds, ownership, name])

  const closeAndNotify = () => {
    toggleModal()

    alwaysNotify.longSuccess(
      `${addPossessive(name)} permissions have been updated.`
    )
  }

  const submitPermissions = () => {
    setProcessing(true)

    const payload = {
      type: isFuneralHome ? 'quarterback-ownerships' : 'firm-ownerships',
      id: ownership.get('id'),
      attributes: {
        'item-permissions': requestedItemPermissions({
          itemIds: checkedItemIds,
          permissionType: 'edit'
        })
      }
    }

    updateResource(payload).then(() => closeAndNotify())
  }

  const onCheck = itemIds => {
    setCheckedItemIds(itemIds)

    setIsDirty(!arraysAreEqual(
      cappedEditItemIds(),
      itemIds
    ))
  }

  return (
    <span className='manage-item-permissions-modal'>
      <Accept onClick={toggleModal}>Manage</Accept>
      <Modals.FullHeightModalLarge
        closerComponent={closer}
        showModal={showModal}>
        <ModalHeader
          heading={`Which sections should ${name} be able to edit?`}
          note={modalHeaderNote}
        />
        <ItemPermissionsSelectForm
          checkedItemIds={checkedItemIds}
          onCheck={onCheck}
          permittedItemIds={ownership.get('manageable-edit-item-ids')}
        />
        <ButtonGroup>
          <Cancel onClick={toggleModal} disabled={processing} />
          <Accept
            disabled={!isDirty}
            onClick={submitPermissions}
            processing={processing}>
            Save
          </Accept>
        </ButtonGroup>
      </Modals.FullHeightModalLarge>
    </span>
  )
}

ManageItemPermissionsModal.propTypes = {
  allItemIds: PropTypes.array.isRequired,
  isFuneralHome: PropTypes.bool,
  name: PropTypes.string,
  ownership: PropTypes.instanceOf(Map),
  updateResource: PropTypes.func
}

export default ManageItemPermissionsModal
